import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaMoneyBillWave,
  FaSearch,
  FaReceipt,
  FaPrint,
  FaCheckCircle,
  FaPaperclip,
  FaTrash,
  FaTimes,
  FaCreditCard,
  FaUniversity,
  FaQrcode,
  FaUserGraduate,
  FaGraduationCap,
  FaLayerGroup,
  FaShieldAlt,
  FaChevronRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import PrintableFeeReceipt from "./PrintableFeeReceipt";
import { classesList } from "../../jsonsFiles/classesList";

export default function CollectFeePage() {
  const { token } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pendingFees, setPendingFees] = useState([]);
  const [selectedFee, setSelectedFee] = useState(null);
  const [loading, setLoading] = useState(false);

  // Search & Filter States
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedSection, setSelectedSection] = useState("ALL");

  // Collection Form State
  const [collectForm, setCollectForm] = useState({
    amountPaid: "",
    discountApplied: 0,
    fineCollected: 0,
    paymentMode: "Cash",
    collectionDate: new Date().toISOString().split("T")[0],
    transactionId: "",
    bankName: "",
    chequeNumber: "",
    chequeDate: "",
    upiId: "",
    remarks: "",
  });

  // Attachment State
  const [attachment, setAttachment] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);

  // Generated Receipt Modal State
  const [generatedReceipt, setGeneratedReceipt] = useState(null);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `FeeReceipt_${generatedReceipt?.receiptNumber || ""}`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; }`,
  });

  // Debounce search input (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Fetch students with API filters
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedClass !== "ALL") params.append("class", selectedClass);
        if (selectedSection !== "ALL")
          params.append("section", selectedSection);
        if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);

        const res = await axios.get(`${server}/students?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudents(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load students");
      }
    };
    if (token) fetchStudents();
  }, [token, selectedClass, selectedSection, debouncedSearchTerm]);

  // Fetch pending fees when student is selected
  useEffect(() => {
    if (!selectedStudent || !token) return;

    const fetchStudentPendingFees = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${server}/student-fees/student/${selectedStudent.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const fees = (res.data.data || []).filter((f) => f.status !== "Paid");
        setPendingFees(fees);
        if (fees.length > 0) {
          setSelectedFee(fees[0]);
          setCollectForm((prev) => ({
            ...prev,
            amountPaid: fees[0].totalAmount - fees[0].paidAmount,
          }));
        } else {
          setSelectedFee(null);
        }
      } catch (error) {
        toast.error("Failed to load student pending fees");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentPendingFees();
  }, [selectedStudent, token]);

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingProof(true);
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${server}/fee-collection/upload-proof`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data.success) {
        setAttachment({
          fileUrl: res.data.fileUrl,
          fileName: res.data.fileName,
          fileSize: res.data.fileSize,
        });
        toast.success("Payment proof uploaded successfully!");
      }
    } catch (error) {
      toast.error("Failed to upload payment proof");
    } finally {
      setUploadingProof(false);
    }
  };

  const handleCollectSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFee) return toast.error("Please select a pending fee bill!");
    if (!collectForm.amountPaid || parseFloat(collectForm.amountPaid) <= 0)
      return toast.error("Please enter a valid amount!");

    try {
      const payload = {
        studentFeeId: selectedFee.id,
        amountPaid: parseFloat(collectForm.amountPaid),
        discountApplied: parseFloat(collectForm.discountApplied || 0),
        fineCollected: parseFloat(collectForm.fineCollected || 0),
        paymentMode: collectForm.paymentMode,
        collectionDate: collectForm.collectionDate,
        transactionId: collectForm.transactionId,
        bankName: collectForm.bankName,
        chequeNumber: collectForm.chequeNumber,
        chequeDate: collectForm.chequeDate,
        upiId: collectForm.upiId,
        remarks: collectForm.remarks,
        attachmentFileUrl: attachment?.fileUrl || "",
        attachmentFileName: attachment?.fileName || "",
        attachmentFileSize: attachment?.fileSize || 0,
      };

      const res = await axios.post(
        `${server}/fee-collection/collect`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.success) {
        toast.success("Fee Collection Processed & Official Receipt Generated!");
        setGeneratedReceipt({
          receiptNumber: res.data.data.receiptNumber,
          receiptDate: collectForm.collectionDate,
          totalAmountPaid: res.data.data.amountPaid,
          paymentMode: res.data.data.paymentMode,
          student: selectedStudent,
          feePayment: {
            studentFee: selectedFee,
          },
          qrCodeData: res.data.data.qrCodeData,
          barcodeData: res.data.data.barcodeData,
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to collect fee",
      );
    }
  };

  const totalDueSelectedFee = selectedFee
    ? selectedFee.totalAmount - selectedFee.paidAmount
    : 0;
  const remainingAfterPayment = Math.max(
    0,
    totalDueSelectedFee - parseFloat(collectForm.amountPaid || 0),
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-xl shadow-xs">
            <FaMoneyBillWave size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
              Fee Collection Desk
            </h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Fast multi-mode student payment settlement console
            </p>
          </div>
        </div>

        {/* Compact Right Status Indicators */}
        <div className="flex items-center gap-3 text-xs">
          <div className="px-3 py-1 bg-gray-100 dark:bg-slate-800 rounded-xl text-gray-600 dark:text-slate-300 font-bold border border-gray-200 dark:border-slate-700">
            Students:{" "}
            <strong className="text-emerald-600 dark:text-emerald-400">
              {students.length}
            </strong>
          </div>
          {selectedStudent && (
            <div className="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-900 rounded-xl text-emerald-700 dark:text-emerald-300 font-bold flex items-center gap-1.5">
              <FaUserGraduate size={11} /> {selectedStudent.studentName} (Cl:{" "}
              {selectedStudent.class}-{selectedStudent.section})
            </div>
          )}
        </div>
      </div>

      {/* Main Compact Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Column: Student Search & Pending Bills (5 Cols) */}
        <div className="lg:col-span-5 space-y-4">
          {/* STEP 1: Student Directory Finder */}
          <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm space-y-3">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-2">
              <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-5 h-5 rounded-md bg-emerald-600 text-white flex items-center justify-center text-[10px]">
                  1
                </span>
                Select Student
              </span>
              {(searchInput !== "" ||
                selectedClass !== "ALL" ||
                selectedSection !== "ALL") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSelectedClass("ALL");
                    setSelectedSection("ALL");
                  }}
                  className="text-[10px] font-bold text-rose-500 hover:underline flex items-center gap-1"
                >
                  <FaTimes size={9} /> Reset Filters
                </button>
              )}
            </div>

            {/* Class & Section Inline Filter Dropdowns */}
            <div className="grid grid-cols-2 gap-2">
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 outline-none focus:border-emerald-500"
              >
                <option value="ALL">All Classes</option>
                {classesList.map((c, idx) => (
                  <option key={idx} value={c}>
                    Class - {c}
                  </option>
                ))}
              </select>

              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 outline-none focus:border-emerald-500"
              >
                <option value="ALL">All Sections</option>
                {["A", "B", "C", "D", "E"].map((sec, idx) => (
                  <option key={idx} value={sec}>
                    Section - {sec}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Input Bar */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
              <input
                type="text"
                placeholder="Search Name, Reg No, Roll No..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-8 pr-8 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-emerald-500"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput("")}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes size={11} />
                </button>
              )}
            </div>

            {/* Compact Student List */}
            <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1 custom-scrollbar">
              {students.length === 0 ? (
                <div className="p-4 text-center text-xs text-gray-400 italic">
                  No matching students
                </div>
              ) : (
                students.map((st) => {
                  const isSelected = selectedStudent?.id === st.id;
                  return (
                    <div
                      key={st.id}
                      onClick={() => {
                        setSelectedStudent(st);
                        setAttachment(null);
                        setGeneratedReceipt(null);
                      }}
                      className={`p-2 rounded-xl border text-xs font-bold transition cursor-pointer flex items-center justify-between ${
                        isSelected
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                          : "bg-gray-50 dark:bg-slate-800/80 border-gray-200 dark:border-slate-700 hover:border-emerald-400"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-6 h-6 rounded-lg text-[10px] font-black flex items-center justify-center ${isSelected ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"}`}
                        >
                          {(st.studentName || "S").charAt(0).toUpperCase()}
                        </div>
                        <span className="truncate max-w-[170px]">
                          {st.studentName}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-medium ${isSelected ? "text-emerald-100" : "text-gray-400 dark:text-slate-400"}`}
                      >
                        Cl: {st.class}-{st.section} • {st.admissionNumber}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* STEP 2: Pending Bills List for Selected Student */}
          {selectedStudent && (
            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm space-y-3">
              <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-2">
                <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-md bg-indigo-600 text-white flex items-center justify-center text-[10px]">
                    2
                  </span>
                  Pending Bills ({pendingFees.length})
                </span>
              </div>

              {loading ? (
                <div className="text-center py-4 text-xs font-bold text-indigo-500">
                  Loading fees...
                </div>
              ) : pendingFees.length === 0 ? (
                <div className="p-4 text-center text-xs font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl">
                  ✓ All fee bills settled for this student!
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingFees.map((fee) => {
                    const isSelected = selectedFee?.id === fee.id;
                    const dueAmt = fee.totalAmount - fee.paidAmount;
                    return (
                      <div
                        key={fee.id}
                        onClick={() => {
                          setSelectedFee(fee);
                          setCollectForm((prev) => ({
                            ...prev,
                            amountPaid: dueAmt,
                          }));
                        }}
                        className={`p-3 rounded-xl border text-xs transition cursor-pointer flex items-center justify-between ${
                          isSelected
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-xs"
                            : "bg-gray-50 dark:bg-slate-800/80 border-gray-200 dark:border-slate-700 hover:border-indigo-400"
                        }`}
                      >
                        <div>
                          <div
                            className={`font-black ${isSelected ? "text-white" : "text-gray-900 dark:text-white"}`}
                          >
                            {fee.feeStructure?.title || "Tuition Fee"} (
                            {fee.month})
                          </div>
                          <div
                            className={`text-[10px] ${isSelected ? "text-indigo-100" : "text-gray-400 dark:text-slate-400"}`}
                          >
                            Due: {new Date(fee.dueDate).toLocaleDateString()}
                          </div>
                        </div>
                        <div
                          className={`font-black text-sm ${isSelected ? "text-white" : "text-emerald-600 dark:text-emerald-400"}`}
                        >
                          ₹{dueAmt.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column: Compact Payment Processing Desk (7 Cols) */}
        <div className="lg:col-span-7">
          {!selectedStudent || !selectedFee ? (
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 p-8 text-center text-gray-400 dark:text-slate-500 shadow-sm flex flex-col items-center justify-center min-h-[320px]">
              <FaReceipt className="text-4xl text-emerald-500 opacity-50 mb-3" />
              <h3 className="text-base font-bold text-gray-800 dark:text-slate-200">
                Select Student & Fee Bill
              </h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 max-w-xs">
                Pick a student and an unpaid fee bill from the left panel to
                open the payment settlement console.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleCollectSubmit}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 p-5 shadow-sm space-y-4"
            >
              <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-3">
                <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-md bg-emerald-600 text-white flex items-center justify-center text-[10px]">
                    3
                  </span>
                  Payment Settlement Console
                </span>
                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950 px-2.5 py-0.5 rounded-md border border-emerald-200 dark:border-emerald-900">
                  Month: {selectedFee.month}
                </span>
              </div>

              {/* Compact Breakdown Grid */}
              <div className="grid grid-cols-3 gap-3 bg-gray-50 dark:bg-slate-800/60 p-3 rounded-xl border border-gray-200 dark:border-slate-700">
                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-0.5">
                    Bill Due
                  </label>
                  <div className="text-lg font-black text-gray-900 dark:text-white">
                    ₹{totalDueSelectedFee.toLocaleString()}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-0.5">
                    Amount Paying *
                  </label>
                  <input
                    type="number"
                    required
                    value={collectForm.amountPaid}
                    onChange={(e) =>
                      setCollectForm({
                        ...collectForm,
                        amountPaid: e.target.value,
                      })
                    }
                    className="w-full px-2.5 py-1 bg-white dark:bg-slate-900 border border-emerald-500 rounded-lg text-sm font-black text-emerald-600 dark:text-emerald-400 outline-none"
                  />
                  <div className="flex gap-1 mt-1">
                    <button
                      type="button"
                      onClick={() =>
                        setCollectForm({
                          ...collectForm,
                          amountPaid: totalDueSelectedFee,
                        })
                      }
                      className="text-[9px] font-extrabold px-1.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 rounded-md"
                    >
                      Full (₹{totalDueSelectedFee})
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCollectForm({
                          ...collectForm,
                          amountPaid: Math.round(totalDueSelectedFee / 2),
                        })
                      }
                      className="text-[9px] font-extrabold px-1.5 py-0.5 bg-gray-200 text-gray-700 dark:bg-slate-700 dark:text-slate-300 rounded-md"
                    >
                      50%
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-0.5">
                    Net Balance
                  </label>
                  <div
                    className={`text-lg font-black ${remainingAfterPayment > 0 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}
                  >
                    ₹{remainingAfterPayment.toLocaleString()}
                  </div>
                  <span className="text-[9px] font-bold text-gray-500 dark:text-slate-400 block mt-0.5">
                    {remainingAfterPayment === 0 ? "✓ Settled" : "Partial"}
                  </span>
                </div>
              </div>

              {/* Payment Mode & Collection Date Row */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-8">
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                    Payment Method *
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "Cash",
                      "UPI",
                      "Credit Card",
                      "Debit Card",
                      "Bank Transfer",
                      "Net Banking",
                      "Cheque",
                    ].map((mode) => {
                      const isSelected = collectForm.paymentMode === mode;
                      return (
                        <button
                          key={mode}
                          type="button"
                          onClick={() =>
                            setCollectForm({
                              ...collectForm,
                              paymentMode: mode,
                            })
                          }
                          className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition flex items-center gap-1.5 ${
                            isSelected
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                              : "bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700 hover:border-emerald-400"
                          }`}
                        >
                          {mode === "Cash" && <FaMoneyBillWave size={11} />}
                          {mode === "UPI" && <FaQrcode size={11} />}
                          {mode.includes("Card") && <FaCreditCard size={11} />}
                          {(mode.includes("Bank") || mode === "Cheque") && (
                            <FaUniversity size={11} />
                          )}
                          {mode}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="sm:col-span-4">
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1">
                    Collection Date
                  </label>
                  <input
                    type="date"
                    value={collectForm.collectionDate}
                    max={new Date(Date.now()).toISOString().split("T")[0]}
                    onChange={(e) =>
                      setCollectForm({
                        ...collectForm,
                        collectionDate: e.target.value,
                      })
                    }
                    className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
                  />
                </div>
              </div>

              {/* Dynamic Payment Details */}
              {collectForm.paymentMode !== "Cash" && (
                <div className="p-3 bg-gray-50 dark:bg-slate-800/60 rounded-xl border border-gray-200 dark:border-slate-700 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  {collectForm.paymentMode === "UPI" && (
                    <div className="sm:col-span-2">
                      <label className="block text-[10px] font-bold text-gray-600 dark:text-slate-300 mb-0.5">
                        UPI ID / Ref
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 9876543210@upi"
                        value={collectForm.upiId}
                        onChange={(e) =>
                          setCollectForm({
                            ...collectForm,
                            upiId: e.target.value,
                          })
                        }
                        className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg font-medium text-gray-900 dark:text-white outline-none"
                      />
                    </div>
                  )}

                  {collectForm.paymentMode === "Cheque" && (
                    <>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-600 dark:text-slate-300 mb-0.5">
                          Cheque No
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. CHQ-485920"
                          value={collectForm.chequeNumber}
                          onChange={(e) =>
                            setCollectForm({
                              ...collectForm,
                              chequeNumber: e.target.value,
                            })
                          }
                          className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg font-medium text-gray-900 dark:text-white outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-600 dark:text-slate-300 mb-0.5">
                          Bank Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. State Bank of India"
                          value={collectForm.bankName}
                          onChange={(e) =>
                            setCollectForm({
                              ...collectForm,
                              bankName: e.target.value,
                            })
                          }
                          className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg font-medium text-gray-900 dark:text-white outline-none"
                        />
                      </div>
                    </>
                  )}

                  {(collectForm.paymentMode.includes("Card") ||
                    collectForm.paymentMode.includes("Bank")) && (
                    <div className="sm:col-span-2">
                      <label className="block text-[10px] font-bold text-gray-600 dark:text-slate-300 mb-0.5">
                        Transaction Ref
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. TXN98745210"
                        value={collectForm.transactionId}
                        onChange={(e) =>
                          setCollectForm({
                            ...collectForm,
                            transactionId: e.target.value,
                          })
                        }
                        className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg font-medium text-gray-900 dark:text-white outline-none"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Remarks & Attachment In a Compact Line */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-0.5">
                    Remarks (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Fee received at counter"
                    value={collectForm.remarks}
                    onChange={(e) =>
                      setCollectForm({
                        ...collectForm,
                        remarks: e.target.value,
                      })
                    }
                    className="w-full px-2.5 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-medium text-gray-900 dark:text-white outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-0.5 flex items-center gap-1">
                    <FaPaperclip /> Payment Proof (Optional)
                  </label>
                  {attachment ? (
                    <div className="flex items-center justify-between p-1.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-lg text-xs font-bold text-emerald-800 dark:text-emerald-300">
                      <span className="truncate max-w-[120px]">
                        {attachment.fileName}
                      </span>
                      <button
                        type="button"
                        onClick={() => setAttachment(null)}
                        className="text-rose-500"
                      >
                        <FaTrash size={11} />
                      </button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleProofUpload}
                      disabled={uploadingProof}
                      className="w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-[10px] file:font-bold file:bg-emerald-100 file:text-emerald-700 transition cursor-pointer"
                    />
                  )}
                </div>
              </div>

              {/* Submit Action Button */}
              <button
                type="submit"
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm rounded-xl transition shadow-md flex items-center justify-center gap-2"
              >
                <FaReceipt /> Process Fee Collection & Issue Receipt
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Generated Receipt Modal */}
      {generatedReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 max-w-4xl w-full shadow-2xl border border-gray-200 dark:border-slate-800 relative space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-3">
              <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaCheckCircle className="text-emerald-500" /> Official Fee
                Receipt Issued
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={handlePrint}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-black hover:bg-emerald-700 flex items-center gap-2 shadow-md"
                >
                  <FaPrint /> Print / Save PDF Receipt
                </button>
                <button
                  onClick={() => setGeneratedReceipt(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1"
                >
                  <FaTimes size={18} />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto flex justify-center py-4 bg-gray-50 dark:bg-slate-950 rounded-2xl border border-gray-200 dark:border-slate-800">
              <PrintableFeeReceipt ref={printRef} receipt={generatedReceipt} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
